import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  inject,
  Input,
  Optional,
  signal,
  OnInit,
  Signal,
  Injector,
} from '@angular/core';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import {
  UserControllerDeleteRequestWebauthnRegisterPasswordlessRemoteMethod
} from 'open-api-legacy/remote-methods/user-controller-delete-request-webauthn-register-passwordless.remote-method';
import {
  UserControllerPutRequestWebauthnRegisterPasswordlessRemoteMethod
} from 'open-api-legacy/remote-methods/user-controller-put-request-webauthn-register-passwordless.remote-method';
import {
  UserControllerGetByUuidDataSource
} from 'open-api-legacy/data-sources/user-controller-get-by-uuid.data-source';
import {
  UserControllerGetWebauthnRegisterPasswordlessDataSource
} from 'open-api-legacy/data-sources/user-controller-get-webauthn-register-passwordless.data-source';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButton, MatIconButton } from '@angular/material/button';
import { DateWithTimeAgoPipe } from 'eurogard-pipes';
import { MatIcon } from '@angular/material/icon';
import { RXAP_WINDOW_DATA } from 'eurogard-window-system';
import { MatDivider } from '@angular/material/divider';
import { MatList, MatListItem, MatListItemIcon, MatListItemLine, MatListItemTitle } from '@angular/material/list';
import { ConfirmDirective } from 'angular-components/confirm/confirm.directive';
import { AsyncPipe, NgIf } from '@angular/common';
import { MatProgressBar } from '@angular/material/progress-bar';
import { FormsModule } from '@angular/forms';
import {
  UserControllerPutRequestConfigureOtpRemoteMethod
} from 'open-api-legacy/remote-methods/user-controller-put-request-configure-otp.remote-method';
import {
  UserControllerDeleteRequestConfigureOtpRemoteMethod
} from 'open-api-legacy/remote-methods/user-controller-delete-request-configure-otp.remote-method';
import { UserControllerGetByUuidResponse } from 'open-api-legacy/responses/user-controller-get-by-uuid.response';
import {
  UserControllerGetWebauthnRegisterPasswordlessResponse
} from 'open-api-legacy/responses/user-controller-get-webauthn-register-passwordless.response';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'eurogard-user-mfa-settings',
  templateUrl: './user-mfa-settings.component.html',
  styleUrls: [ './user-mfa-settings.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [],
  standalone: true,
  imports: [
    MatSlideToggle,
    MatButton,
    DateWithTimeAgoPipe,
    MatIcon,
    MatDivider,
    MatIconButton,
    MatList,
    MatListItem,
    ConfirmDirective,
    MatListItemIcon,
    MatListItemLine,
    MatListItemTitle,
    AsyncPipe,
    MatProgressBar,
    NgIf,
    FormsModule,
  ],
})
export class UserMfaSettingsComponent implements OnInit {
  @Input() userUuid!: string;

  protected readonly isLoading = signal(false);

  protected userData!: Signal<UserControllerGetByUuidResponse | undefined>;
  protected readonly userDataSource = inject(UserControllerGetByUuidDataSource);

  protected passKeyData!: Signal<UserControllerGetWebauthnRegisterPasswordlessResponse | undefined>;
  protected readonly passKeyDataSource = inject(UserControllerGetWebauthnRegisterPasswordlessDataSource);

  private readonly toggle2FAOn = inject(UserControllerPutRequestConfigureOtpRemoteMethod);
  private readonly toggle2FAOff = inject(UserControllerDeleteRequestConfigureOtpRemoteMethod);
  private readonly userControllerPutRequestWebauthnRegisterPasswordlessRemoteMethod = inject(UserControllerPutRequestWebauthnRegisterPasswordlessRemoteMethod);
  private readonly userControllerDeleteRequestWebauthnRegisterPasswordlessRemoteMethod = inject(UserControllerDeleteRequestWebauthnRegisterPasswordlessRemoteMethod)

  private readonly matSnackBar = inject(MatSnackBar);
  private injector = inject(Injector);

  constructor(
    @Inject(RXAP_WINDOW_DATA)
    @Optional()
    data: { uuid: string },
  ) {
    if (data) {
      this.userUuid = data.uuid;
    }
  }

  ngOnInit(): void {
    this.userData = toSignal(
      this.userDataSource.connect({
        id: 'to-signal-user-controller-getByUuid',
        parameters: { uuid: this.userUuid },
      }),
      { injector: this.injector }
    );

    this.passKeyData = toSignal(
      this.passKeyDataSource.connect({
        id: 'to-signal-user-controller-getWebauthnRegisterPasswordless',
        parameters: { 'user-uuid': this.userUuid },
      }),
      { injector: this.injector }
    );
  }

  protected toggleTwoFactor(toggleOn: boolean): void {
    this.isLoading.set(true);

    if (toggleOn) {
      this.toggle2FAOn.call({
        parameters: { userUuid: this.userUuid },
      }).then(() => {
        this.matSnackBar.open($localize`Two Factor Authentication toggled ON successfully. You will be requested to set it up with your next login`, $localize`Close`, { duration: 5000 });
      }).catch(() => {
        this.matSnackBar.open($localize`Unable to toggle ON Two Factor Authentication`, $localize`Close`, { duration: 3000 });
      }).finally(() => {
        this.userDataSource.refresh();
        this.passKeyDataSource.refresh();
        this.isLoading.set(false);
      });
    } else {
      this.toggle2FAOff.call({
        parameters: { userUuid: this.userUuid },
      }).then(() => {
        this.matSnackBar.open($localize`Two Factor Authentication toggled OFF successfully`, $localize`Close`, { duration: 3000 });
      }).catch(() => {
        this.matSnackBar.open($localize`Unable to toggle OFF Two Factor Authentication`, $localize`Close`, { duration: 3000 });
      }).finally(() => {
        this.userDataSource.refresh();
        this.passKeyDataSource.refresh();
        this.isLoading.set(false);
      });
    }
  }

  protected requestPasskey(): void {
    this.isLoading.set(true);
    this.userControllerPutRequestWebauthnRegisterPasswordlessRemoteMethod.call({
      parameters: { userUuid: this.userUuid }
    }).then(() => {
      this.matSnackBar.open($localize`You will be requested to set it up at next login`, $localize`Close`, { duration: 5000 });
    }).catch(() => {
      this.matSnackBar.open($localize`Failed to request passkey`, $localize`Close`, { duration: 3000 });
    }).finally(() => {
      this.userDataSource.refresh();
      this.passKeyDataSource.refresh();
      this.isLoading.set(false);
    });
  }

  protected deletePasskeyRequest(): void {
    this.isLoading.set(true);
    this.userControllerDeleteRequestWebauthnRegisterPasswordlessRemoteMethod.call({
      parameters: { userUuid: this.userUuid, 'client-id': null }
    }).then(() => {
      this.matSnackBar.open($localize`Passkey request deleted successfully`, $localize`Close`, { duration: 3000 });
    }).catch(() => {
      this.matSnackBar.open($localize`Failed to delete passkey request`, $localize`Close`, { duration: 3000 });
    }).finally(() => {
      this.userDataSource.refresh();
      this.passKeyDataSource.refresh();
      this.isLoading.set(false);
    });
  }

  protected deletePassKey(clientId: string | undefined): void {
    if (!clientId) return;
    this.isLoading.set(true);
    this.userControllerDeleteRequestWebauthnRegisterPasswordlessRemoteMethod.call({
      parameters: { userUuid: this.userUuid, 'client-id': clientId }
    }).then(() => {
      this.matSnackBar.open($localize`Passkey deleted successfully`, $localize`Close`, { duration: 3000 });
    }).catch(() => {
      this.matSnackBar.open($localize`Failed to delete passkey`, $localize`Close`, { duration: 3000 });
    }).finally(() => {
      this.userDataSource.refresh();
      this.passKeyDataSource.refresh();
      this.isLoading.set(false);
    });
  }
}
